/* eslint-disable @typescript-eslint/no-explicit-any */
import { fetchOrderDetail } from '@/apis/order/order.api';
import { IOrderDetailReq, IOrderDetailRes } from '@/apis/order/order.interface';
import UKPageError from '@/components/UKPageError';
import UKPageLoading from '@/components/UKPageLoading';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';
import { useTranslation } from '@/i18n/client';
import to from 'await-to-js';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import OrderDetailAmount from '../orderDetailAmount';
import OrderDetailProduct from '../orderDetailProduct';
import OrderDetailStatus from '../orderDetailStatus';

interface orderDetailComponentsProps {
  orderId: string;
  isHideFloor?: boolean;
  pageLoadedHandler?: (productSkuIds: string[]) => void;
  changePageHandler?: (value: boolean) => void;
}

const orderDetailComponents: React.FC<orderDetailComponentsProps> = observer(
  ({ orderId, isHideFloor, pageLoadedHandler, changePageHandler }) => {
    const { t } = useTranslation();

    const [orderDetails, setOrderDetails] = useState<IOrderDetailRes | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    if (!orderId) {
      return null;
    }

    const loadOrderDetails = async () => {
      const requestData: IOrderDetailReq = { orderId: String(orderId) };
      const [err, orderDetailData] = await to(fetchOrderDetail(requestData));

      if (err) {
        setError('Failed to fetch order details');
        setOrderDetails(null);
      } else {
        if (orderDetailData?.orderStatusDTO?.recycle) {
          setOrderDetails(null);
          changePageHandler && changePageHandler(true);
        } else {
          // // 过滤详情中的服务品
          // const filterOrderDetailData = {
          //   ...orderDetailData,
          //   wareInfoDTOList: orderDetailData?.wareInfoDTOList.filter(
          //     (ele) => ele?.wareType !== "1",
          //   ),
          // };
          // setOrderDetails(filterOrderDetailData as IOrderDetailRes);
          setOrderDetails(orderDetailData as IOrderDetailRes);
          changePageHandler && changePageHandler(false);
        }
        setError(null);
      }
      const wareList = orderDetailData?.wareInfoDTOList.map((item) => item?.wareId) || [];
      pageLoadedHandler && pageLoadedHandler(wareList as any);
      setLoading(false);
    };

    useEffect(() => {
      loadOrderDetails();
    }, [orderId]);
    const {
      orderStatusDTO = {},
      buttonInfoDTOList = [],
      orderIdInfoDTO,
      deliveryinfoDTO,
      paymentInfoDTO,
      invoiceInfoDTO,
      addressInfoDTO,
      priceInfoDTO = {},
      wareInfoDTOList = [],
      logisticsInfoDTO = {},
      showCancelReason,
      showAfsProgressInfo,
      showCancelInfo,
      cancelInfoDTO = {},
      orderTagContentVO,
      localInfo,
      pointVO,
    } = orderDetails || {};
    const { orderStatusId } = orderStatusDTO || {};
    const { postCode = '' } = addressInfoDTO || {};

    if (loading) {
      return <UKPageLoading style={{ backgroundColor: 'transparent' }} />;
    }

    // 订单明细数据处理
    const orderDetailDataHandler = () => {
      // JDISVOrderListOrderStatusTypeWaitingPay = 1, //待支付、等待付款确认
      // JDISVOrderListOrderStatusTypeWaitingDelivery = 3, //待发货
      // JDISVOrderListOrderStatusTypeCompleted = 5, //已完成
      // JDISVOrderListOrderStatusTypeCancelled = 6, //已取消
      // JDISVOrderListOrderStatusTypeWaitingSelfDelivery = 7, //等待自提
      // JDISVOrderListOrderStatusTypeWaitingReceipt = 8, //待收货
      let list = null;
      if (orderStatusId === '1') {
        // Unpaid
        list = [
          // Order ID
          {
            label: orderIdInfoDTO?.orderIdTitle || '',
            value: orderIdInfoDTO?.orderId || '',
            showCopyButton: orderIdInfoDTO?.showCopyButton,
            copyButtonTitle: orderIdInfoDTO?.copyButtonTitle,
          },
          // Delivery address
          {
            label: deliveryinfoDTO?.isSelfPickupOrder
              ? deliveryinfoDTO?.addressTitle
              : addressInfoDTO?.addressTitle || '',
            value: deliveryinfoDTO?.isSelfPickupOrder
              ? deliveryinfoDTO?.selfPickupAddress
              : addressInfoDTO?.address || '',
            customerName: deliveryinfoDTO?.isSelfPickupOrder
              ? deliveryinfoDTO?.selfPickupName
              : addressInfoDTO?.customerName,
            mobile: deliveryinfoDTO?.isSelfPickupOrder ? '' : addressInfoDTO?.mobile, // pickup订单不展示联系方式
          },
          // Billing address
          {
            label: invoiceInfoDTO?.invoiceAddressTitle || '',
            value: invoiceInfoDTO?.invoiceAddress || '',
            customerName: invoiceInfoDTO?.invoiceUserName,
            mobile: invoiceInfoDTO?.invoiceUserPhone,
            isExpand: true,
          },
        ];
      } else if (
        orderStatusId === '3' ||
        orderStatusId === '8' ||
        orderStatusId === '6' ||
        orderStatusId === '5'
      ) {
        // Processing Shipped Cancelled Completed
        list = [
          // Order ID
          {
            label: orderIdInfoDTO?.orderIdTitle || '',
            value: orderIdInfoDTO?.orderId || '',
            showCopyButton: orderIdInfoDTO?.showCopyButton,
            copyButtonTitle: orderIdInfoDTO?.copyButtonTitle,
          },
          // Order time
          {
            // label: orderIdInfoDTO?.submitDateTitle || "",
            // value: orderIdInfoDTO?.submitDate || "",
            label: paymentInfoDTO?.paymentTimeTitle || '',
            value: paymentInfoDTO?.paymentTime || '',
          },
          // Payment method
          {
            label: paymentInfoDTO?.paymentTitle || '',
            value: paymentInfoDTO?.payMode || '',
            ...(paymentInfoDTO?.showType === 1 &&
              paymentInfoDTO?.jumpUrl && {
                jumpUrl: paymentInfoDTO?.jumpUrl,
              }),
          },
          // Delivery address
          {
            label: deliveryinfoDTO?.isSelfPickupOrder
              ? deliveryinfoDTO?.addressTitle
              : addressInfoDTO?.addressTitle || '',
            value: deliveryinfoDTO?.isSelfPickupOrder
              ? deliveryinfoDTO?.selfPickupAddress
              : addressInfoDTO?.address || '',
            customerName: deliveryinfoDTO?.isSelfPickupOrder
              ? deliveryinfoDTO?.selfPickupName
              : addressInfoDTO?.customerName,
            mobile: deliveryinfoDTO?.isSelfPickupOrder ? '' : addressInfoDTO?.mobile, // pickup订单不展示联系方式
          },
          // Billing address
          {
            label: invoiceInfoDTO?.invoiceAddressTitle || '',
            value: invoiceInfoDTO?.invoiceAddress || '',
            // customerName: invoiceInfoDTO?.customerName,
            // mobile: invoiceInfoDTO?.secretMobile?.plain,
            customerName: invoiceInfoDTO?.invoiceUserName,
            mobile: invoiceInfoDTO?.invoiceUserPhone,
            isExpand: true,
          },
        ];
      } else if (orderStatusId === '7') {
        list = [
          // Order ID
          {
            label: orderIdInfoDTO?.orderIdTitle || '',
            value: orderIdInfoDTO?.orderId || '',
            showCopyButton: orderIdInfoDTO?.showCopyButton,
            copyButtonTitle: orderIdInfoDTO?.copyButtonTitle,
          },
          // Order time
          {
            // label: orderIdInfoDTO?.submitDateTitle || "",
            // value: orderIdInfoDTO?.submitDate || "",
            label: paymentInfoDTO?.paymentTimeTitle || '',
            value: paymentInfoDTO?.paymentTime || '',
          },
          // Payment method
          {
            label: paymentInfoDTO?.paymentTitle || '',
            value: paymentInfoDTO?.payMode || '',
            ...(paymentInfoDTO?.showType === 1 &&
              paymentInfoDTO?.jumpUrl && {
                jumpUrl: paymentInfoDTO?.jumpUrl,
              }),
          },
          // Billing address
          {
            label: invoiceInfoDTO?.invoiceAddressTitle || '',
            value: invoiceInfoDTO?.invoiceAddress || '',
            // customerName: invoiceInfoDTO?.customerName,
            // mobile: invoiceInfoDTO?.secretMobile?.plain,
            customerName: invoiceInfoDTO?.invoiceUserName,
            mobile: invoiceInfoDTO?.invoiceUserPhone,
            isExpand: true,
          },
        ];
      }

      return list;
    };

    if (error) {
      return (
        <UKPageError
          style={{ padding: '60px 0' }}
          text={t('order.orderList.errorPage.tip')}
          btnText={t('order.orderList.errorPage.btnTip')}
          clickTryAgain={() => {
            window.location.reload();
          }}
        />
      );
    }

    if (!orderDetails) {
      return (
        <UKPageNothing
          defaultImageType={DefaultImageType.DEFAULT_NOTING}
          style={{ padding: '60px 0' }}
          text={t('order.orderList.emptyPage.orderTip')}
        />
      );
    }

    return (
      <>
        {/* 订单状态楼层 */}
        {isHideFloor ? null : (
          <OrderDetailStatus
            orderInfo={{
              orderId,
              ...orderDetails,
            }}
            onRefresh={loadOrderDetails}
          />
        )}
        {/* 订单金额信息+非金额信息楼层 */}
        <OrderDetailAmount
          orderInfo={{
            detailInfo: orderDetailDataHandler(),
            priceInfoDTO,
            orderStatusDTO,
            orderId,
            localInfo,
            pointVO,
          }}
        />
        {/* 订单商品信息楼层 */}
        <OrderDetailProduct
          orderInfo={{
            orderStatusDTO,
            wareInfoDTOList,
            orderId,
            postCode,
            orderTagContentVO,
            localInfo,
          }}
          onRefresh={loadOrderDetails}
        />
      </>
    );
  },
);

export default orderDetailComponents;
